/**
 * useSituationalEffects - the acting character's CONDITIONAL effects,
 * applied to the attack about to be made.
 *
 * An effect with a condition ("+10 WS when the target is Hulking or
 * larger") is not resolved with the sheet: it holds for one situation. This
 * is the one place that situation is put together - the armed weapon's
 * kind, the current target (size, origin classes, tags, conditions, wounds,
 * whether the scene says they have not noticed), the actor's own state
 * (conditions, wounds, fatigue, frenzy, the stances up), the attack's
 * toggles and the armed weapon (its rules, damage type and firing mode) - and
 * the one place the effects are applied (`engine/character/situational`).
 *
 * It runs where the scene's unawareness AND the map are known (the Map
 * domain, plan armoury: a condition may ask the range band, the distance or
 * the target's cover), which is after the attacker's own modifiers were summed, so like `useSceneUnaware` it hands
 * back that sum and its list with its lines added, and the attacker's stats
 * with any conditional damage or re-roll. With no such effect those keys are
 * absent and the scope keeps what it had.
 *
 * It also judges the TALENTS that hold against a kind of target (Hatred,
 * Hunter of Aliens, Scourge of Heretics): `hatred` and `rerollDamage` come
 * back true while one holds or the manual switch is on, with the reason in
 * `autoHatred` / `autoRerollDamage` for the switch to show.
 *
 * @param {object} args - actor, slot, isMelee, fireMode, attackToggles, roster, rosterTabs, activeTab,
 *   unawareIds, rangeBand, liveMap, attackerCharStats, effectiveCustomMods, attackMod, hatred,
 *   rerollDamage (the two manual switches).
 * @returns the replaced keys - see above.
 */
import { useMemo } from 'react';
import { attackEffects, situate } from '../engine/character/situational';
import { selfFactsOf } from '../engine/character/selfFacts';
import { talentRulings } from '../engine/character/talentRulings';
import { effectiveSheet } from '../engine/roster/stancedSheet';
import { originClassesOf } from '../engine/threatAssessment';
import { EMPTY_WEAPON_OVERRIDES, applyWeaponOverrides } from '../engine/weaponOverrides';
import { readWeaponRules, situateRules } from '../engine/weaponRules';

const NONE = [];
const NOTHING = { waitingEffects: NONE };
const NO_RULINGS = { hatred: null, rerollDamage: null };

/** Who is attacked, as a condition may ask about them; null with nobody targeted. */
function targetOf({ roster, rosterTabs, activeTab, unawareIds }) {
  const target = rosterTabs?.find((t) => t.id === activeTab)?.target;
  if (!target) return null;
  const sheet = roster?.find((c) => c.id === activeTab)?.sheet ?? null;
  return {
    size: target.size,
    conditions: target.conditions,
    origins: originClassesOf(target, sheet),
    tags: sheet?.tags ?? NONE,
    isHorde: !!target.horde?.isHorde,
    unaware: !!unawareIds?.has(activeTab),
    ...(target.wounds ? { wounds: target.wounds } : {}),
    critical: !!target.criticalDamage?.length,
  };
}

/** The actor as a clause may ask about them (plan armoury): the one answer the effective sheet hears too. */
const selfOf = (actor) => selfFactsOf(actor?.sheet, actor?.state);

/** The armed weapon as a clause may ask about it; absent with none armed. A blade has no firing mode. */
function weaponOf({ slot, isMelee, fireMode }) {
  if (!slot) return undefined;
  return {
    specials: slot.specialRules ?? NONE,
    tags: slot.tags ?? NONE,
    ...(slot.damageType ? { damageType: slot.damageType } : {}),
    ...(isMelee || !fireMode ? {} : { mode: fireMode }),
  };
}

/** Where it happens (plan armoury): the band rolled at, the distance and the target's cover, map or tab. */
function placeOf({ rangeBand, liveMap, rosterTabs, activeTab }) {
  if (!rangeBand) return undefined;
  const tabCover = rosterTabs?.find((t) => t.id === activeTab)?.target?.cover?.level;
  const cover = liveMap?.cover?.level ?? tabCover;
  return {
    band: rangeBand,
    metres: liveMap?.measurement?.metres ?? null,
    ...(cover ? { cover } : {}),
  };
}

/** The situation of the next attack. With no weapon armed its kind is not known. */
export function situationOf(args) {
  const place = placeOf(args);
  return {
    attack: args.slot ? (args.isMelee ? 'melee' : 'ranged') : undefined,
    target: targetOf(args),
    self: selfOf(args.actor),
    ...(args.attackToggles ? { action: args.attackToggles } : {}),
    ...(args.slot ? { weapon: weaponOf(args) } : {}),
    ...(place ? { place } : {}),
  };
}

/** The situation of the next attack, rebuilt only when something it reads changes. */
function useSituation(args) {
  const { actor, slot, isMelee, fireMode, attackToggles, roster, rosterTabs, activeTab, unawareIds } = args;
  const { rangeBand, liveMap } = args;
  // The actor changes identity on every write; the situation reads only its state and sheet.
  const state = actor?.state;
  const sheet = actor?.sheet;
  return useMemo(
    () =>
      situationOf({
        actor: state ? { state, sheet } : null,
        ...{ slot, isMelee, fireMode, attackToggles, roster, rosterTabs, activeTab, unawareIds, rangeBand, liveMap },
      }),
    [
      state,
      sheet,
      slot,
      isMelee,
      fireMode,
      attackToggles,
      roster,
      rosterTabs,
      activeTab,
      unawareIds,
      rangeBand,
      liveMap,
    ],
  );
}

/** The sum, its list and the stats with what the stances' attack effects add; null when there are none. */
function useSituated(sheet, situation, { attackerCharStats, effectiveCustomMods, attackMod }) {
  // The effective sheet is one object per (sheet, stances up), so this list is stable between edits.
  const effects = useMemo(() => (sheet ? attackEffects(sheet) : NONE), [sheet]);
  const situated = useMemo(
    () => (effects.length ? situate(attackerCharStats, effects, situation) : null),
    [effects, situation, attackerCharStats],
  );
  return useMemo(() => {
    if (!situated) return null;
    const added = situated.mods.reduce((sum, mod) => sum + mod.value, 0);
    return {
      attackerCharStats: situated.stats,
      effectiveCustomMods: situated.mods.length ? [...effectiveCustomMods, ...situated.mods] : effectiveCustomMods,
      attackMod: attackMod + added,
      waitingEffects: situated.waiting,
      rerollDamage: situated.rerollDamage,
    };
  }, [situated, effectiveCustomMods, attackMod]);
}

/** Damage a holding rule adds, on the stats the hit reads: to the kind of attack the weapon makes. */
function withDamage(stats, damage, isMelee) {
  if (!stats || !damage) return stats;
  const key = isMelee ? 'damageBonusMelee' : 'damageBonusRanged';
  return { ...stats, [key]: (stats[key] ?? 0) + damage };
}

/**
 * The armed weapon's conditional rules (plan armoury, `engine/weaponRules`), applied on top of what the stances
 * gave: their to-hit lines and damage, and `attackOverrides` - the table's weapon overrides with the rules'
 * patch merged in - with the `effectiveSlot` it makes. `weaponOverrides` itself is never replaced: the overrides
 * panel keeps showing (and saving) only what the table set. Null when no rule holds.
 */
function useWeaponRules(args, situation, stanced) {
  const { slot, weaponOverrides, isMelee } = args;
  const rules = useMemo(() => readWeaponRules(slot?.rules), [slot?.rules]);
  return useMemo(() => {
    if (!rules.length || !slot) return null;
    const base = weaponOverrides ?? EMPTY_WEAPON_OVERRIDES;
    const out = situateRules(rules, situation, base);
    if (out.overrides === base && !out.mods.length && !out.damage) return null;
    const mods = stanced?.effectiveCustomMods ?? args.effectiveCustomMods;
    const added = out.mods.reduce((sum, mod) => sum + mod.value, 0);
    return {
      effectiveCustomMods: out.mods.length ? [...mods, ...out.mods] : mods,
      attackMod: (stanced?.attackMod ?? args.attackMod) + added,
      attackerCharStats: withDamage(stanced?.attackerCharStats ?? args.attackerCharStats, out.damage, isMelee),
      attackOverrides: out.overrides,
      effectiveSlot: applyWeaponOverrides(slot, out.overrides),
    };
  }, [
    rules,
    slot,
    weaponOverrides,
    isMelee,
    situation,
    stanced,
    args.effectiveCustomMods,
    args.attackMod,
    args.attackerCharStats,
  ]);
}

export default function useSituationalEffects(args) {
  const { actor, hatred, rerollDamage } = args;
  const sheet = actor ? effectiveSheet(actor) : null;
  const situation = useSituation(args);
  const stanced = useSituated(sheet, situation, args);
  const ruled = useWeaponRules(args, situation, stanced);
  const situated = useMemo(() => (ruled ? { ...(stanced ?? NOTHING), ...ruled } : stanced), [ruled, stanced]);
  // Hatred, Hunter of Aliens, Scourge of Heretics: judged against the target (engine/character/talentRulings).
  const rulings = useMemo(() => (sheet ? talentRulings(sheet, situation) : NO_RULINGS), [sheet, situation]);
  return useMemo(() => {
    const { rerollDamage: stanceReroll = null, ...replaced } = situated ?? NOTHING;
    const autoRerollDamage = rulings.rerollDamage ?? stanceReroll;
    return {
      ...replaced,
      // The manual switches still count: the table can always overrule. `auto...` is the reason, for the switch.
      hatred: !!hatred || !!rulings.hatred,
      rerollDamage: !!rerollDamage || !!autoRerollDamage,
      autoHatred: rulings.hatred,
      autoRerollDamage,
    };
  }, [situated, rulings, hatred, rerollDamage]);
}
